"use client";

import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { Button, Input, Label, cn, confirm, toast } from "@devhop/ui";
import {
  ChevronDownIcon,
  ExternalLinkIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { WorkshopJobDetail, WorkshopJobPartRow } from "../api/client";
import {
  useAddWorkshopJobPartMutation,
  usePatchWorkshopJobPartMutation,
  useRemoveWorkshopJobPartMutation,
  useWorkshopFitmentSkusQuery,
  useWorkshopVehicleConfigurationQuery,
} from "../api/queries";
import {
  formatWorkshopMoney,
  inventoryStateToneClass,
  WORKSHOP_INVENTORY_STATE_LABELS,
  WORKSHOP_PART_SOURCE_LABELS,
} from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";
import { WorkshopCustomerSuppliedPartForm } from "./WorkshopCustomerSuppliedPartForm";
import type { WorkshopPartAddSourceMode } from "./WorkshopPartSourceChoice";
import { WorkshopQtyStepper } from "./WorkshopQtyStepper";
import { WorkshopVehicleConfigPicker } from "./WorkshopVehicleConfigPicker";

const MARKETPLACE_ORIGIN =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_MARKETPLACE_URL?: string } }).env
      ?.VITE_MARKETPLACE_URL) ||
  "http://localhost:3002";

function marketplaceSearchUrl(opts: {
  q?: string;
  configurationId?: string | null;
}) {
  const url = new URL("/search", MARKETPLACE_ORIGIN);
  if (opts.q) url.searchParams.set("q", opts.q);
  if (opts.configurationId) {
    url.searchParams.set("configurationId", opts.configurationId);
  }
  return url.toString();
}

export function WorkshopJobPartsPanel({
  shopId,
  job,
  canEdit,
  defaultAddOpen = false,
  addSourceMode = "all",
  dialogMode = false,
}: {
  shopId: string;
  job: WorkshopJobDetail;
  canEdit: boolean;
  /** เปิดฟอร์มเพิ่มอะไหล่ทันที (ใช้ใน dialog) */
  defaultAddOpen?: boolean;
  /** กรอง UI เพิ่มอะไหล่ — shop | customer | all */
  addSourceMode?: WorkshopPartAddSourceMode | "all";
  /** โหมด dialog: ซ่อนปุ่ม toggle, ไม่แสดง list ซ้ำถ้าต้องการ */
  dialogMode?: boolean;
}) {
  const [q, setQ] = useState("");
  const [fitmentOnly, setFitmentOnly] = useState(true);
  const [orderDrafts, setOrderDrafts] = useState<Record<string, string>>({});
  const [addOpen, setAddOpen] = useState(defaultAddOpen);
  const [fitmentOpen, setFitmentOpen] = useState(!job.vehicleConfigurationId);
  const [skuQtyDrafts, setSkuQtyDrafts] = useState<Record<string, number>>({});
  const { currentShop } = useMerchantShops(shopId);
  const inventoryEnabled = hasShopFeature(
    currentShop?.featureFlags,
    "inventory",
  );

  const configSummaryQ = useWorkshopVehicleConfigurationQuery(
    shopId,
    job.vehicleConfigurationId,
  );

  const fitmentQuery = useMemo(
    () => ({
      branchId: job.branchId,
      vehicleConfigurationId: job.vehicleConfigurationId || undefined,
      q: q.trim() || undefined,
      fitmentOnly: fitmentOnly && Boolean(job.vehicleConfigurationId),
      limit: 40,
    }),
    [job.branchId, job.vehicleConfigurationId, q, fitmentOnly],
  );

  const needsSkuSearch =
    addOpen &&
    (addSourceMode === "shop" ||
      addSourceMode === "all" ||
      (addSourceMode === "customer" && Boolean(job.vehicleConfigurationId)));

  const skusQ = useWorkshopFitmentSkusQuery(
    shopId,
    needsSkuSearch ? fitmentQuery : undefined,
  );
  const addMut = useAddWorkshopJobPartMutation(shopId);
  const patchMut = usePatchWorkshopJobPartMutation(shopId);
  const removeMut = useRemoveWorkshopJobPartMutation(shopId);

  const parts = job.parts ?? [];

  function skuQty(skuId: string, maxAvailable?: number) {
    const raw = skuQtyDrafts[skuId] ?? 1;
    if (maxAvailable != null && maxAvailable >= 1) {
      return Math.min(raw, maxAvailable);
    }
    return raw;
  }

  function setSkuQty(skuId: string, qty: number, maxAvailable?: number) {
    const next =
      maxAvailable != null && maxAvailable >= 1
        ? Math.min(qty, maxAvailable)
        : qty;
    setSkuQtyDrafts((prev) => ({ ...prev, [skuId]: next }));
  }

  async function addStock(skuId: string, qty: number) {
    try {
      await addMut.mutateAsync({
        jobId: job.id,
        body: { shopProductSkuId: skuId, qty, source: "stock" },
      });
      toast.success("ເພີ່ມອາໄຫຼ່ + ຈອງສະຕ໋ອກແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function addCustomerSupplied(skuId: string, qty: number) {
    try {
      await addMut.mutateAsync({
        jobId: job.id,
        body: {
          shopProductSkuId: skuId,
          qty,
          source: "customer_supplied",
          unitPrice: 0,
        },
      });
      toast.success("ເພີ່ມອາໄຫຼ່ລູກຄ້ານຳມາແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function addCustomerSuppliedFreeText(args: {
    name: string;
    qty: number;
  }) {
    try {
      await addMut.mutateAsync({
        jobId: job.id,
        body: {
          name: args.name,
          qty: args.qty,
          source: "customer_supplied",
          unitPrice: 0,
        },
      });
      toast.success("ເພີ່ມອາໄຫຼ່ລູກຄ້ານຳມາແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  // ປິດ Marketplace ໄວ້ຊົ່ວຄາວ (comment ຕາມຄຳຂໍ) — ໃຊ້ຄູ່ກັບປຸ່ມ Marketplace ທີ່ comment ໄວ້
  // async function addMarketplace(skuId: string, name: string, qty: number) {
  //   try {
  //     await addMut.mutateAsync({
  //       jobId: job.id,
  //       body: {
  //         shopProductSkuId: skuId,
  //         qty,
  //         source: "marketplace",
  //         setWaitingParts: true,
  //       },
  //     });
  //     window.open(
  //       marketplaceSearchUrl({
  //         q: name,
  //         configurationId: job.vehicleConfigurationId,
  //       }),
  //       "_blank",
  //       "noopener,noreferrer",
  //     );
  //     toast.success("ເພີ່ມແຖວ + ຕັ້ງລໍຖ້າອາໄຫຼ່ — ເປີດ Marketplace ແລ້ວ");
  //   } catch (err) {
  //     toast.error(workshopToastError(err));
  //   }
  // }

  async function saveOrderId(part: WorkshopJobPartRow) {
    const orderId = (
      orderDrafts[part.id] ??
      part.marketplaceOrderId ??
      ""
    ).trim();
    try {
      await patchMut.mutateAsync({
        jobId: job.id,
        partId: part.id,
        body: { marketplaceOrderId: orderId || null },
      });
      toast.success("ບັນທຶກ order id ແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function removePart(part: WorkshopJobPartRow) {
    const ok = await confirm({
      title: "ລຶບອາໄຫຼ່",
      description: `ລຶບ «${part.name}» (× ${part.qty}) ອອກຈາກໃບງານ ຫຼື ບໍ່?`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await removeMut.mutateAsync({ jobId: job.id, partId: part.id });
      toast.success("ລຶບອາໄຫຼ່ + ປົດຈອງແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function updatePartQty(part: WorkshopJobPartRow, qty: number) {
    if (qty === part.qty) return;
    if (part.inventoryState === "consumed") {
      toast.error("ຕັດສະຕ໋ອກແລ້ວ — ປ່ຽນຈຳນວນບໍ່ໄດ້");
      return;
    }
    try {
      await patchMut.mutateAsync({
        jobId: job.id,
        partId: part.id,
        body: { qty },
      });
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  const showCustomerAdd = addSourceMode === "customer" || addSourceMode === "all";
  const showShopAdd = addSourceMode === "shop" || addSourceMode === "all";

  function renderPartRow(p: WorkshopJobPartRow, compact?: boolean) {
    const qtyEditable =
      canEdit && p.inventoryState !== "consumed" && p.inventoryState !== "released";

    return (
      <li
        key={p.id}
        className={cn(
          "border-border border-b py-3 last:border-b-0",
          compact && "py-2.5",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                "font-medium leading-snug",
                compact ? "text-sm" : "text-sm",
              )}
            >
              {p.name}
            </p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {WORKSHOP_PART_SOURCE_LABELS[p.source] ?? p.source}
            </p>
            {!compact ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <span
                  className={cn(
                    "inline-flex rounded-md border px-1.5 py-0.5 text-[11px] font-medium",
                    inventoryStateToneClass(p.inventoryState),
                  )}
                >
                  {WORKSHOP_INVENTORY_STATE_LABELS[p.inventoryState]}
                </span>
                {p.source === "marketplace" ? (
                  <span className="inline-flex rounded-md border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-900">
                    ສັ່ງ Marketplace
                  </span>
                ) : null}
                {p.source === "customer_supplied" ? (
                  <span className="inline-flex rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-900">
                    ລູກຄ້ານຳມາ
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {qtyEditable ? (
              <WorkshopQtyStepper
                size="sm"
                value={p.qty}
                disabled={patchMut.isPending}
                onChange={(qty) => void updatePartQty(p, qty)}
              />
            ) : (
              <span className="text-muted-foreground text-xs">× {p.qty}</span>
            )}
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm tabular-nums">
                {p.source === "customer_supplied" && p.lineTotal === 0 ? (
                  <span className="text-violet-800 text-xs">ລູກຄ້ານຳມາ</span>
                ) : (
                  formatWorkshopMoney(p.lineTotal, p.currency)
                )}
              </p>
              {canEdit ? (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8 text-muted-foreground hover:text-destructive"
                  onClick={() => void removePart(p)}
                  aria-label="ລຶບ"
                >
                  <Trash2Icon className="size-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        {!compact &&
        canEdit &&
        (p.source === "marketplace" || p.marketplaceOrderId) ? (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-40 flex-1 space-y-1">
              <Label className="text-xs">ເລກອໍເດີ Marketplace</Label>
              <Input
                value={orderDrafts[p.id] ?? p.marketplaceOrderId ?? ""}
                onChange={(e) =>
                  setOrderDrafts((prev) => ({
                    ...prev,
                    [p.id]: e.target.value,
                  }))
                }
                placeholder="ຕິດ order id"
                className="h-9"
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void saveOrderId(p)}
            >
              ບັນທຶກ
            </Button>
          </div>
        ) : p.marketplaceOrderId && !compact ? (
          <p className="mt-1.5 font-mono text-muted-foreground text-xs">
            order: {p.marketplaceOrderId}
          </p>
        ) : null}
      </li>
    );
  }

  return (
    <div className="space-y-3">
      {parts.length === 0 ? (
        <p className="py-2 text-muted-foreground text-xs">
          ຍັງບໍ່ມີອາໄຫຼ່ໃນໃບງານ
        </p>
      ) : (
        <ul className="space-y-0">
          {parts.map((p) => renderPartRow(p, dialogMode))}
        </ul>
      )}

      {canEdit ? (
        <>
          {showShopAdd ? (
            <>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-border px-3 py-3 text-left transition hover:border-primary/40 hover:bg-muted/30"
                onClick={() => setFitmentOpen((o) => !o)}
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm">ລຸ້ນລົດ (fitment)</p>
                  {job.vehicleConfigurationId ? (
                    <p className="mt-0.5 truncate text-muted-foreground text-xs">
                      {configSummaryQ.data?.displayName ?? "ກຳລັງໂຫຼດ…"}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-amber-700 text-xs">
                      ແຕະເພື່ອເລືອກຍີ່ຫໍ້ → ລຸ້ນ → ປີ/ເຄື່ອງ
                    </p>
                  )}
                </div>
                <ChevronDownIcon
                  className={cn(
                    "size-4 shrink-0 text-muted-foreground transition",
                    fitmentOpen && "rotate-180",
                  )}
                />
              </button>
              {fitmentOpen ? (
                <div className="rounded-xl border border-border bg-muted/15 p-3">
                  <WorkshopVehicleConfigPicker
                    shopId={shopId}
                    jobId={job.id}
                    vehicleConfigurationId={job.vehicleConfigurationId}
                    onSaved={() => setFitmentOpen(false)}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {!dialogMode ? (
            <Button
              type="button"
              variant={addOpen ? "secondary" : "outline"}
              className="h-11 w-full"
              onClick={() => setAddOpen((o) => !o)}
            >
              <PlusIcon className="size-4" />
              {addOpen ? "ປິດຟອມເພີ່ມ" : "ເພີ່ມອາໄຫຼ່"}
            </Button>
          ) : null}

          {addOpen ? (
            <div className="space-y-2 rounded-xl border border-border bg-muted/15 p-3">
              {showCustomerAdd ? (
                <WorkshopCustomerSuppliedPartForm
                  disabled={addMut.isPending}
                  onAdd={addCustomerSuppliedFreeText}
                />
              ) : null}
              {showShopAdd && !inventoryEnabled ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-950 text-xs">
                  ຮ້ານປິດສາງ — ເພີ່ມຈາກສາງບໍ່ໄດ້. ໃຊ້ Marketplace / ລູກຄ້ານຳມາ
                  ຫຼືເພີ່ມຄ່າແຮງແທນ
                </p>
              ) : null}
              {showShopAdd && !job.vehicleConfigurationId ? (
                <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-amber-900 text-xs">
                  ແນະນຳ: ເລືອກລຸ້ນລົດກ່ອນ ເພື່ອກອງອາໄຫຼ່ທີ່ fit
                </p>
              ) : null}
              {showShopAdd ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      className="min-w-48 flex-1"
                      placeholder="ຄົ້ນຫາ SKU / ຊື່"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      autoFocus
                    />
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={fitmentOnly}
                        onChange={(e) => setFitmentOnly(e.target.checked)}
                        disabled={!job.vehicleConfigurationId}
                      />
                      ສະເພາະທີ່ fit ລົດ
                    </label>
                  </div>
                  {skusQ.isLoading ? (
                    <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດ…</p>
                  ) : (
                    <ul className="max-h-64 space-y-1.5 overflow-y-auto">
                      {(skusQ.data?.data ?? []).map((sku) => (
                        <li
                          key={sku.shopProductSkuId}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-2.5 py-2 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{sku.name}</p>
                            <p className="text-muted-foreground text-xs">
                              {sku.sku} · ວາງຂາຍ {sku.available} ·{" "}
                              {formatWorkshopMoney(sku.price, sku.currency)}
                              {sku.fitsConfiguration ? " · ໃຊ້ກັບລົດນີ້" : ""}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <WorkshopQtyStepper
                              size="sm"
                              value={skuQty(
                                sku.shopProductSkuId,
                                inventoryEnabled && sku.available >= 1
                                  ? sku.available
                                  : undefined,
                              )}
                              max={
                                inventoryEnabled && sku.available >= 1
                                  ? sku.available
                                  : undefined
                              }
                              onChange={(n) =>
                                setSkuQty(
                                  sku.shopProductSkuId,
                                  n,
                                  inventoryEnabled && sku.available >= 1
                                    ? sku.available
                                    : undefined,
                                )
                              }
                            />
                            <div className="flex flex-wrap justify-end gap-1">
                              {inventoryEnabled && sku.available >= 1 ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={addMut.isPending}
                                  onClick={() =>
                                    void addStock(
                                      sku.shopProductSkuId,
                                      skuQty(
                                        sku.shopProductSkuId,
                                        sku.available,
                                      ),
                                    )
                                  }
                                >
                                  ເພີ່ມຈາກສາງ
                                </Button>
                              ) : null}
                              {addSourceMode === "all" ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={addMut.isPending}
                                  onClick={() =>
                                    void addCustomerSupplied(
                                      sku.shopProductSkuId,
                                      skuQty(sku.shopProductSkuId),
                                    )
                                  }
                                >
                                  ລູກຄ້ານຳມາ
                                </Button>
                              ) : null}
                              {/* ປິດປຸ່ມ Marketplace ໄວ້ຊົ່ວຄາວ (comment ຕາມຄຳຂໍ)
                              <Button
                                type="button"
                                size="sm"
                                variant={
                                  !inventoryEnabled || sku.available < 1
                                    ? "default"
                                    : "outline"
                                }
                                disabled={addMut.isPending}
                                onClick={() =>
                                  void addMarketplace(
                                    sku.shopProductSkuId,
                                    sku.name,
                                    skuQty(sku.shopProductSkuId),
                                  )
                                }
                              >
                                <ExternalLinkIcon className="size-3.5" />
                                Marketplace
                              </Button>
                              */}
                            </div>
                          </div>
                        </li>
                      ))}
                      {(skusQ.data?.data ?? []).length === 0 ? (
                        <div className="space-y-2 py-2 text-center">
                          <p className="text-muted-foreground text-xs">
                            ບໍ່ພົບສິນຄ້າໃນຮ້ານ
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              window.open(
                                marketplaceSearchUrl({
                                  q: q.trim() || undefined,
                                  configurationId: job.vehicleConfigurationId,
                                }),
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            <ExternalLinkIcon className="size-3.5" />
                            ຄົ້ນໃນ Marketplace
                          </Button>
                        </div>
                      ) : null}
                    </ul>
                  )}
                </>
              ) : null}
              {showCustomerAdd &&
              addSourceMode === "customer" &&
              job.vehicleConfigurationId ? (
                <>
                  <p className="border-border border-t pt-3 text-muted-foreground text-xs">
                    ຜູກກັບ catalog (ຖ້າມີ SKU ໃນລະບົບ)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      className="min-w-48 flex-1"
                      placeholder="ຄົ້ນຈາກ catalog"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  {skusQ.isLoading ? (
                    <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດ…</p>
                  ) : (
                    <ul className="max-h-40 space-y-1 overflow-y-auto">
                      {(skusQ.data?.data ?? []).slice(0, 15).map((sku) => (
                        <li
                          key={sku.shopProductSkuId}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-2.5 py-2 text-sm"
                        >
                          <span className="min-w-0 truncate">{sku.name}</span>
                          <div className="flex items-center gap-2">
                            <WorkshopQtyStepper
                              size="sm"
                              value={skuQty(sku.shopProductSkuId)}
                              onChange={(n) =>
                                setSkuQty(sku.shopProductSkuId, n)
                              }
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={addMut.isPending}
                              onClick={() =>
                                void addCustomerSupplied(
                                  sku.shopProductSkuId,
                                  skuQty(sku.shopProductSkuId),
                                )
                              }
                            >
                              + ລູກຄ້ານຳມາ
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
